import { campaignPackageReviewSchema } from '@borradh-workspace/contracts/claire';
import type { UIMessage } from 'ai';
import { asToolPart, getToolName, hasToolResultFailure } from './tool-parts';

const record = (value: unknown): Record<string, unknown> | undefined =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;

function readReadyPackageApproval(
  parts: UIMessage['parts']
): { packageIndex: number; adIds: Set<string> } | undefined {
  let packageIndex = -1;
  for (let index = parts.length - 1; index >= 0; index--) {
    const part = asToolPart(parts[index]);
    if (part && getToolName(part) === 'meta_ads_launchCampaignPackage') {
      packageIndex = index;
      break;
    }
  }
  if (packageIndex < 0) return undefined;
  const packagePart = asToolPart(parts[packageIndex]);
  if (
    !packagePart ||
    packagePart.state !== 'output-available' ||
    hasToolResultFailure(packagePart)
  )
    return undefined;
  const presentation = record(packagePart.output?.presentation);
  if (
    presentation?.type !== 'confirmation_required' ||
    presentation.action !== 'launch_campaign_package' ||
    presentation.executeToolName !== 'meta_ads_launchCampaignPackage' ||
    typeof presentation.resourceId !== 'string' ||
    !presentation.resourceId ||
    presentation.resourceId !== packagePart.input?.campaignId ||
    typeof presentation.token !== 'string' ||
    !presentation.token ||
    !Array.isArray(packagePart.input?.adIds)
  )
    return undefined;
  const review = campaignPackageReviewSchema.safeParse(
    record(presentation.summary)?.campaignPackage
  );
  if (!review.success) return undefined;
  const adIds = new Set(review.data.ads.map((ad) => ad.adId));
  if (
    review.data.ads.length !== packagePart.input.adIds.length ||
    new Set(packagePart.input.adIds).size !== packagePart.input.adIds.length ||
    adIds.size !== packagePart.input.adIds.length ||
    !packagePart.input.adIds.every(
      (id) => typeof id === 'string' && adIds.has(id)
    )
  )
    return undefined;

  return { packageIndex, adIds };
}

/** The final saved review replaces interim ad previews in this message only. */
export function getPackageSupersededAdPreviewToolCallIds(
  parts: UIMessage['parts']
): Set<string> {
  const superseded = new Set<string>();
  const ready = readReadyPackageApproval(parts);
  if (!ready) return superseded;
  const { packageIndex, adIds } = ready;
  for (const part of parts.slice(0, packageIndex)) {
    const toolPart = asToolPart(part);
    if (
      !toolPart ||
      toolPart.state !== 'output-available' ||
      hasToolResultFailure(toolPart)
    )
      continue;
    const output = toolPart.output;
    const card = record(output?.presentation);
    if (
      card?.type === 'ad_preview' &&
      record(card.preview)?.variant === 'draft' &&
      typeof output?.adId === 'string' &&
      adIds.has(output.adId) &&
      typeof output.confirmationToken !== 'string'
    )
      superseded.add(toolPart.toolCallId);
  }
  return superseded;
}

/** Keep preparation prose available inside the completed package's tool trace. */
export function getPackagePreparationNotes(message: UIMessage): {
  partIndexes: ReadonlySet<number>;
  texts: string[];
} {
  const partIndexes = new Set<number>();
  const texts: string[] = [];
  const ready = readReadyPackageApproval(message.parts);
  if (message.role !== 'assistant' || !ready) return { partIndexes, texts };
  for (const [index, part] of message.parts.entries()) {
    const toolPart = asToolPart(part);
    if (
      toolPart &&
      (index > ready.packageIndex ||
        toolPart.state !== 'output-available' ||
        hasToolResultFailure(toolPart))
    )
      return { partIndexes, texts };
  }

  // Stored content aggregates every step's text and is hydrated after tools.
  // It does not prove post-review prose: the engine ends at a ready package.
  // Live parts retain their real order, so actual post-package text stays visible.
  const storedSummary =
    record(message.metadata)?.textOrder === 'stored-summary';
  for (const [index, part] of message.parts.entries()) {
    if (part.type === 'text' && (storedSummary || index < ready.packageIndex)) {
      partIndexes.add(index);
      texts.push(part.text);
    }
  }
  return { partIndexes, texts };
}
